import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Heading from '../../components/Heading';
import UploadBulk from './components/UploadBulk';
import { DraftContact } from 'models/Contact';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  AddContactsPeopleTableKeys,
  CHECKBOX_COLUMNS,
  CUSTOM_COL_SIZES,
} from './components/ManageContactTableCells/data';
import { Link, useNavigate } from 'react-router-dom';
import { Loader } from 'ncoded-component-library';
import {
  BODY_CONTENT_GENERATE,
  HEADER_CONTENT_GENERATE,
  KeyCell,
  KeyHeader,
} from './components/ManageContactTableCells';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import ManageDraftContactDrawer from './components/ManageDraftContactDrawer';
import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { updateKeyInObject } from 'utils';
import {
  checkIfCanProcessColumn,
  checkIfColumnIsValid,
  checkIsNotIgnoreColumn,
  checkHasEveryRequiredColumn,
} from './utils';
import BulkAploadActions from 'components/BulkAploadActions';
import { CONTACTS_ROUTE } from 'data/global';

import './AddContacts.styles.scss';

type AddContactsProps = {
  className?: string;
};

const AddContacts: React.FC<AddContactsProps> = (props) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState<string[]>(CHECKBOX_COLUMNS);
  const [selectedContact, setSelectedContact] = useState<DraftContact>();
  const [contacts, setContacts] = useState<DraftContact[]>([]);
  const [manageContactDrawer, manageContactDrawerRef] =
    useCallbackRef<SideDrawerRef>();

  const classes = classNames('add-contacts', className);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const count = useMemo(() => contacts?.length, [contacts?.length]);

  const areAllContactApproved = useMemo(
    () => contacts.every(({ preApproved }) => preApproved),
    [contacts],
  );

  const numberOfProcessableColumns = useMemo(
    () => columns.filter(checkIfCanProcessColumn).length,
    [columns],
  );

  const numberOfUnProcessableColumns = useMemo(
    () => columns.length - numberOfProcessableColumns - CHECKBOX_COLUMNS.length,
    [columns, numberOfProcessableColumns],
  );

  const canSubmitContacts = useMemo(
    () => checkHasEveryRequiredColumn(columns) && !!count,
    [columns, count],
  );

  const updateContact = useCallback(
    ({ _id, ...rest }) =>
      setContacts((old) =>
        old.map((el) => (el._id === _id ? { ...el, ...rest } : el)),
      ),
    [],
  );

  const onPreApproveContact = useCallback(
    ({ _id, preApproved }) => updateContact({ _id, preApproved: !preApproved }),
    [updateContact],
  );

  const onApproveAll = useCallback(
    () =>
      setContacts((old) =>
        old.map(({ preApproved, ...rest }) => ({
          ...rest,
          preApproved: !areAllContactApproved,
        })),
      ),
    [areAllContactApproved],
  );

  const onUpdateColumn = useCallback((key: string, newKey: string) => {
    setColumns((old) => old.map((el) => (el === key ? newKey : el)));
    setContacts((old) =>
      old.map((el) => updateKeyInObject(el, key, newKey) as DraftContact),
    );
  }, []);

  const renderedContent = useMemo(
    () =>
      columns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (contact: DraftContact) => {
            const { _id } = contact;

            const isPreApproved = contacts?.find(
              (el) => el._id === _id,
            )?.preApproved;

            if (checkIfColumnIsValid(col))
              return BODY_CONTENT_GENERATE[col as AddContactsPeopleTableKeys]({
                contact,
                isPreApproved,
                column: col,
                onPreApproveContact,
              });

            return <KeyCell column={col} contact={contact} />;
          },
        }),
        {},
      ),
    [columns, contacts, onPreApproveContact],
  );

  const renderedHeader = useMemo(
    () =>
      columns.reduce(
        (acc, col, index) => ({
          ...acc,
          [col]: () => {
            if (checkIfColumnIsValid(col))
              return HEADER_CONTENT_GENERATE[col as AddContactsPeopleTableKeys](
                {
                  column: col,
                  index,
                  allColumns: columns,
                  areAllContactApproved,
                  onApproveAll,
                  onUpdateColumn,
                },
              );

            return (
              <KeyHeader
                column={col}
                index={index}
                allColumns={columns}
                onUpdateColumn={onUpdateColumn}
              />
            );
          },
        }),
        {},
      ),
    [columns, areAllContactApproved, onApproveAll, onUpdateColumn],
  );

  const clearContacts = useCallback(() => {
    setSelectedContact(null);
    setColumns([]);
    setContacts([]);
  }, []);

  const handleEditContact = useCallback(
    (contact: DraftContact) => {
      setSelectedContact(contact);
      manageContactDrawer.setOpen(true);
    },
    [manageContactDrawer],
  );

  const handleDeleteContact = useCallback(
    (id: string) => setContacts((old) => old.filter((el) => el._id !== id)),
    [],
  );

  const handleOnUpload = useCallback((uploaded: DraftContact[]) => {
    const keys = Object.keys(uploaded[0]).filter(checkIsNotIgnoreColumn);

    setColumns([...CHECKBOX_COLUMNS, ...keys]);
    setContacts(uploaded);
  }, []);

  const handleBulkUpload = useCallback(async () => {
    const selectedContacts = contacts.map(({ _id, ...rest }) =>
      Object.fromEntries(
        Object.entries(rest).filter(([key]) => checkIfColumnIsValid(key)),
      ),
    );

    try {
      setIsLoading(true);
      await api.contacts.bulkCreateContacts(selectedContacts);
      popSuccess(
        t('contactsImportedSuccesfully', {
          count,
        }),
      );
      navigate('../');
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [contacts, count, popSuccess, t, navigate, popServerError]);

  return (
    <div className={classes}>
      <Heading title={t('contacts')}>
        <Link to={CONTACTS_ROUTE}>{t('goToContactsPage')}</Link>
      </Heading>

      {isLoading && <Loader className="fixed" />}

      {!contacts.length ? (
        <UploadBulk onUpload={handleOnUpload} />
      ) : (
        <div className="white-panel">
          <h2>{t('bulkUploadContacts', { count })}</h2>

          <ReorderResizeTable
            columns={columns}
            isDragDisabled
            isResizeDisabled
            data={contacts}
            customSizes={CUSTOM_COL_SIZES}
            tableContentTransformations={renderedContent}
            tableHeaderContentTransformations={renderedHeader}
            onRowClick={handleEditContact}
          />

          <BulkAploadActions
            title="uploadContacts"
            total={count}
            isDisabled={!canSubmitContacts}
            numberOfProcessableColumns={numberOfProcessableColumns}
            numberOfUnProcessableColumns={numberOfUnProcessableColumns}
            onClear={clearContacts}
            handleBulkUpload={handleBulkUpload}
          />

          <ManageDraftContactDrawer
            contact={selectedContact}
            ref={manageContactDrawerRef}
            onSuccess={updateContact}
            onDelete={handleDeleteContact}
          />
        </div>
      )}
    </div>
  );
};

export default AddContacts;
